import React, {useCallback, useEffect, useMemo, useState} from 'react';
import useLookUpBase from "../selectbox/LookupBase";
import {Popup} from "devextreme-react/popup";
import Guid from "../../models/Guid";
import Form, {ButtonItem, EmptyItem, GroupItem, Label, SimpleItem} from "devextreme-react/form";
import PopupFichierJoint from './popup-fichier-joint';
import {Button} from "devextreme-react/button";
import {HtmlEditor, MediaResizing, TableContextMenu} from 'devextreme-react/html-editor';
import './popup-courriel.css'
import IEnvoitCourrielParams from "../../models/IEnvoitCourrielParams";
import IMessage from "../../models/IMessage";
import useApi from "../../hooks/useApi";
import IFichierJoint from "../../models/IFichierJoint";
import ILookupItem from "../../models/ILookupItem";
import IMessageFichier from "../../models/IMessageFichier";

interface IPopupProps {
    to?: string,
    from?: string,
    noAuxiliaire: string,
    togglePopup: (e: boolean) => void,
    onSend: (e: IEnvoitCourrielParams) => void
    fichiersJoint?: IFichierJoint[],
    defaultIdMessageCourriel?: Guid
    defaultIdMessageConfirmation?: Guid
}

const sizeValues = ['8pt', '10pt', '12pt', '14pt', '18pt', '24pt', '36pt'];
const fontValues = [
    'Arial',
    'Courier New',
    'Georgia',
    'Impact',
    'Lucida Console',
    'Tahoma',
    'Times New Roman',
    'Verdana',
];
const headerValues = [false, 1, 2, 3, 4, 5];
const fontSizeOptions = {
    inputAttr: {
        'aria-label': 'Font size',
    },
};
const fontFamilyOptions = {
    inputAttr: {
        'aria-label': 'Font family',
    },
};
const headerOptions = {
    inputAttr: {
        'aria-label': 'Font family',
    },
};

const PopupCourriel = ({to, noAuxiliaire, togglePopup, onSend, fichiersJoint, defaultIdMessageConfirmation, defaultIdMessageCourriel}: IPopupProps) => {
    const {currentSelectedItem, editorOptions: messagesEditorOption, selectBox: MessagesSelectBox} = useLookUpBase<IMessage & ILookupItem>({cacheKey: "messageCourriel", controllerName: "message", setDataRow: () => setDataRow("idMessage"), path: "lookup/e", showFind: false, showClear: false});
    const {editorOptions: confirmationMessageOptions, currentSelectedItem: currentSelectedConfirmationMessageItem} = useLookUpBase<IMessage & ILookupItem>({showClear: false, cacheKey: "messageConfirmation", controllerName: "message", path: "lookup/c"})
    const [fichiersVisible, setFichiersVisible] = useState(false);
    const {getData: getMessage} = useApi<IMessage>("message");
    const {getData: getMessageFichierJoint} = useApi<IMessageFichier[]>("message");

    const [envoitCourrielParams, setEnvoitCourrielParams] = useState<IEnvoitCourrielParams & { idMessagePredefini?: Guid }>({
        body: "",
        fichiersJoint: fichiersJoint || [],
        idReservationEntete: Guid.empty,
        title: `Confirmation de réservation ${noAuxiliaire}`,
        to: to || "",
        idMessageConfirmation: defaultIdMessageConfirmation,
        idMessagePredefini: defaultIdMessageCourriel
    });

    const confirmBtnOptions = useMemo(() => {
        return {
            width: 120,
            text: "Envoyer",
            onClick: async () => {
                return onSend(envoitCourrielParams);
            }
        };
    }, [envoitCourrielParams, onSend]);
    const onHiding = useCallback(() => togglePopup(false), [togglePopup]);
    const annulerBtnOptions = useMemo(() => ({
        text: "Annuler",
        onClick: onHiding
    }), [onHiding]);

    const adresseOptions = useMemo(() => ({
        text: envoitCourrielParams.to
    }), [envoitCourrielParams.to]);

    const objetOptions = useMemo(() => ({
        text: `Confirmation de réservation ${noAuxiliaire}`
    }), [noAuxiliaire]);

    const fichierJointOptions = useMemo(() => ({
        text: `Fichier joint ${envoitCourrielParams.fichiersJoint?.length > 0 ? `(${envoitCourrielParams.fichiersJoint?.length})` : ""}`,
        onClick: () => {
            setFichiersVisible(prev => !prev)
        }
    }), [envoitCourrielParams.fichiersJoint?.length]);

    const onBodyValueChanged = useCallback((e: any) => {
        setEnvoitCourrielParams(prev => ({...prev, body: e.value, idMessagePredefini: currentSelectedItem?.texteFr != prev.body ? Guid.empty : prev.idMessagePredefini}))
    }, [currentSelectedItem?.texteFr])

    const setFiles = (fichier: IFichierJoint) => {

        setEnvoitCourrielParams((prev) => ({...prev, fichiersJoint: [...prev.fichiersJoint.filter(f => f.name !== fichier.name), {...fichier, id: fichier.id || new Guid()}]}))
    }

    useEffect(() => {
        getMessage({customPath: "message/getDefaultMessage/e", returnOnly: true}).then(message => {
            setEnvoitCourrielParams(prev => ({...prev, idMessagePredefini: message.idMessage, body: currentSelectedItem?.texteFr || prev.body}))
            getMessageFichierJoint({customPath: `message/${message.idMessage}/fichiersjoint`, returnOnly: true}).then(fichiers => {
                fichiers.forEach(f => setFiles({id: f.idMessageFichier, isFromBackEnd: true, name: f.nomFichier} as IFichierJoint));
            })
        })
        getMessageFichierJoint({customPath: `message/${defaultIdMessageConfirmation}/fichiersjoint`, returnOnly: true}).then(fichiers => {
            fichiers.forEach(f => setFiles({id: f.idMessageFichier, isFromBackEnd: true, name: f.nomFichier} as IFichierJoint));
        })
    }, []);

    useEffect(() => {
        if (!currentSelectedConfirmationMessageItem || currentSelectedConfirmationMessageItem?.idMessage?.equals(Guid.empty)) return;
        getMessageFichierJoint({customPath: `message/${currentSelectedConfirmationMessageItem.idMessage}/fichiersjoint`, returnOnly: true}).then(fichiers => {
            fichiers.forEach(f => setFiles({id: f.idMessageFichier, isFromBackEnd: true, name: f.nomFichier}));
        })
    }, [currentSelectedConfirmationMessageItem]);

    useEffect(() => {
        setEnvoitCourrielParams(prev => ({...prev, body: currentSelectedItem?.texteFr || prev.body}))
    }, [currentSelectedItem?.texteFr]);

    const htmlEditorToolbar = useMemo(() => ({
        items: [
            {name: "background"},
            {name: "bold"},
            {name: "color"},
            {name: "italic"},
            {name: "link"},
            {name: "image"},
            {name: "strike"},
            {name: "subscript"},
            {name: "superscript"},
            {name: "underline"},
            {name: "blockquote"},
            {name: "header", acceptedValues: headerValues, options: headerOptions},
            {name: "increaseIndent"},
            {name: "decreaseIndent"},
            {name: "orderedList"},
            {name: "bulletList"},
            {name: "alignLeft"},
            {name: "alignCenter"},
            {name: "alignRight"},
            {name: "alignJustify"},
            {name: "codeBlock"},
            {name: "variable"},
            {name: "font", acceptedValues: fontValues, options: fontFamilyOptions},
            {name: "size", acceptedValues: sizeValues, options: fontSizeOptions},
            {name: "undo"},
            {name: "redo"},
            {name: "clear"},
            {name: "separator"},
            {name: "cellProperties"},
            {name: "tableProperties"},
            {name: "insertTable"},
            {name: "insertHeaderRow"},
            {name: "insertRowAbove"},
            {name: "insertRowBelow"},
            {name: "insertColumnLeft"},
            {name: "insertColumnRight"},
            {name: "deleteColumn"},
            {name: "deleteRow"},
            {name: "deleteTable"}
        ]
    }), []);

    return <Popup
        visible={true}
        dragEnabled={true}
        resizeEnabled={true}
        hideOnOutsideClick={false}
        showCloseButton={true}
        showTitle={true}
        dragOutsideBoundary={true}
        title="Envoi de courriel"
        container=".dx-viewport"
        width={750}
        height={600}
        onHiding={onHiding}
    >
        <div style={{display: "flex", width: "100%", height: "100%", padding: "2px"}}>
            <div style={{flex: 1, display: "flex", flexDirection: "column"}}>
                <Form style={{height:"auto"}} formData={envoitCourrielParams} readOnly={false} alignItemLabelsInAllGroups={true} className={"forms"} labelLocation={"left"} showColonAfterLabel={true}>
                    <GroupItem>
                        <GroupItem>
                            <SimpleItem dataField={"idMessageConfirmation"}
                                        cssClass='remove-padding'
                                        editorType={"dxSelectBox"}
                                        editorOptions={confirmationMessageOptions}>
                                <Label text={"Message sur la confirmation"}/>
                            </SimpleItem>
                        </GroupItem>
                        <SimpleItem key={"type-message"} dataField={"to"} editorOptions={adresseOptions}>
                            <Label text={"Adresse"}/>
                        </SimpleItem>
                        <GroupItem colCount={3}>
                            <SimpleItem colSpan={2} key={"type-message2"} dataField={"title"}
                                        editorOptions={objetOptions}>
                                <Label text={"Objet"}/>
                            </SimpleItem>
                            <GroupItem cssClass=''>
                                <ButtonItem buttonOptions={fichierJointOptions}/>
                            </GroupItem>
                        </GroupItem>
                        <SimpleItem dataField={"idMessagePredefini"} key={"frm-message-predefini"}
                                    cssClass='remove-padding'
                                    editorType={"dxSelectBox"}
                                    editorOptions={messagesEditorOption}
                        >
                            <Label text={"Message prédéfini"}/>
                        </SimpleItem>
                        <EmptyItem/>
                    </GroupItem>
                </Form>
                <HtmlEditor
                    style={{flex: 1,overflow: "hidden"}}
                    toolbar={htmlEditorToolbar}
                    value={envoitCourrielParams?.body?.replaceAll('\r\n','</br>')}
                    onValueChanged={onBodyValueChanged}
                />
                <div style={{display: "flex", paddingBottom: "1rem", marginTop: "1rem", gap: "1rem", justifyContent: "end", alignItems: "end"}}>
                    <Button style={{padding: "0 1rem"}} {...confirmBtnOptions}/>
                    <Button style={{padding: "0 1rem"}} {...annulerBtnOptions}/>
                </div>
            </div>
        </div>
        {fichiersVisible &&
            <PopupFichierJoint
                fichiers={envoitCourrielParams.fichiersJoint}
                setFiles={setFiles}
                close={() => {
                    setFichiersVisible(false)
                }}
            />}
    </Popup>
}

export default PopupCourriel;

function setDataRow(arg0: string) {
    throw new Error('Function not implemented.');
}
